/**
 * Touch haptic feedback for buttons, navigation, and cart actions.
 * Android: Vibration API. iOS 17.4+: hidden checkbox switch trick.
 */
class ThemeHaptics {
  static #lastTrigger = 0;
  static #pointerStart = null;
  static #minInterval = 45;
  static #tapThreshold = 12;

  static #patterns = {
    selection: 8,
    light: 10,
    medium: 18,
    heavy: 28,
    success: [12, 40, 12],
    warning: [14, 35, 14],
    error: [10, 55, 10, 55, 10],
  };

  static #interactiveSelector = [
    'button:not([disabled]):not(.disabled)',
    'a[href]',
    'summary',
    '[role="button"]:not([aria-disabled="true"])',
    'input[type="submit"]:not([disabled])',
    'input[type="button"]:not([disabled])',
    'input[type="reset"]:not([disabled])',
    '.quantity__button:not(.disabled)',
    '[data-haptic]:not([data-haptic="none"])',
  ].join(', ');

  static #excludeSelector = [
    'input:not([type="submit"]):not([type="button"]):not([type="reset"])',
    'textarea',
    'select',
    '[contenteditable="true"]',
    '[data-no-haptic]',
    'label',
  ].join(', ');

  static isEnabled() {
    if (typeof prefersReducedMotion === 'function' && prefersReducedMotion()) return false;
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  }

  static play(style = 'light') {
    if (!this.isEnabled()) return;

    const now = performance.now();
    if (now - this.#lastTrigger < this.#minInterval) return;
    this.#lastTrigger = now;

    const pattern = this.#patterns[style] ?? this.#patterns.light;

    try {
      if (typeof navigator.vibrate === 'function' && navigator.vibrate(pattern)) return;
      this.#triggerIosSwitch();
    } catch (_error) {
      /* Haptics are optional — never interrupt UX */
    }
  }

  static #triggerIosSwitch() {
    const label = document.createElement('label');
    label.setAttribute('aria-hidden', 'true');
    label.hidden = true;

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.setAttribute('switch', '');
    label.appendChild(input);

    document.body.appendChild(label);
    label.click();
    document.body.removeChild(label);
  }

  static #resolveStyle(element) {
    const override = element.dataset.haptic;
    if (override && override !== 'none') return override;

    if (
      element.matches(
        '[type="submit"], .button--primary, .product-form__submit, .cart__checkout-button, .shopify-payment-button__button, .quick-add__submit'
      )
    ) {
      return 'medium';
    }

    if (element.matches('cart-remove-button, [data-cart-remove], .cart-item__remove, .button--tertiary')) {
      return 'medium';
    }

    if (
      element.matches(
        '.lego-drawer-mobile-tab, [data-drawer-parent], [data-secondary-target], [data-mobile-panel], [data-secondary-back]'
      )
    ) {
      return 'selection';
    }

    if (element.matches('.quantity__button, [name="minus"], [name="plus"]')) {
      return 'light';
    }

    if (element.matches('.slider-button, .slideshow__control, .pagination__item')) {
      return 'light';
    }

    if (element.matches('input[type="checkbox"], input[type="radio"], .variant-picker label, .product-form__input label')) {
      return 'selection';
    }

    if (element.matches('summary, .header__icon, .menu-drawer__menu-item')) {
      return 'light';
    }

    if (element.matches('a[href]')) {
      return 'selection';
    }

    if (element.matches('button, [role="button"]')) {
      return 'light';
    }

    return 'light';
  }

  static #findTarget(element) {
    if (!element || element.closest(this.#excludeSelector)) return null;

    const target = element.closest(this.#interactiveSelector);
    if (!target || target.closest(this.#excludeSelector)) return null;

    if (target.matches('a[href]') && target.getAttribute('href') === '#') return null;
    if (target.matches('button[disabled], button.disabled, [aria-disabled="true"]')) return null;

    return target;
  }

  static #onPointerDown(event) {
    if (!this.isEnabled()) return;
    if (event.pointerType === 'mouse') return;

    this.#pointerStart = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
  }

  static #onPointerUp(event) {
    if (!this.isEnabled() || !this.#pointerStart) return;
    if (event.pointerType === 'mouse') return;
    if (event.pointerId !== this.#pointerStart.id) return;

    const deltaX = Math.abs(event.clientX - this.#pointerStart.x);
    const deltaY = Math.abs(event.clientY - this.#pointerStart.y);
    this.#pointerStart = null;

    if (deltaX > this.#tapThreshold || deltaY > this.#tapThreshold) return;

    const target = this.#findTarget(event.target);
    if (!target) return;

    this.play(this.#resolveStyle(target));
  }

  static #onPointerCancel() {
    this.#pointerStart = null;
  }

  static init() {
    if (!this.isEnabled()) return;

    document.addEventListener('pointerdown', this.#onPointerDown.bind(this), { capture: true, passive: true });
    document.addEventListener('pointerup', this.#onPointerUp.bind(this), { capture: true, passive: true });
    document.addEventListener('pointercancel', this.#onPointerCancel.bind(this), { capture: true, passive: true });

    document.addEventListener(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        const source = event.detail?.source;
        if (!source || source === 'cart-items') return;
        this.play('success');
      },
      { passive: true }
    );

    document.addEventListener(
      PUB_SUB_EVENTS.cartError,
      () => {
        this.play('error');
      },
      { passive: true }
    );
  }
}

document.addEventListener('DOMContentLoaded', () => ThemeHaptics.init());
